import React, { createContext, useContext, useState, useEffect } from "react";

interface ThemeContextValue {
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = "ezcloud_theme";
const STORAGE_EXPLICIT_KEY = "ezcloud_theme_explicit";

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const [isDark, setIsDark] = useState<boolean>(() => {
    // Only honour the stored preference if the user explicitly toggled it
    if (localStorage.getItem(STORAGE_EXPLICIT_KEY) === "true") {
      return localStorage.getItem(STORAGE_KEY) === "dark";
    }
    return false;
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    localStorage.setItem(STORAGE_KEY, isDark ? "dark" : "light");
  }, [isDark]);

  const toggleTheme = () => {
    localStorage.setItem(STORAGE_EXPLICIT_KEY, "true");
    setIsDark((prev) => !prev);
  };

  return <ThemeContext.Provider value={{ isDark, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export const useThemeMode = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useThemeMode must be used within a ThemeContextProvider");
  }
  return context;
};
